import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useSearchParams } from "react-router-dom";
import { ReportService } from "../../../services/ReportService";
import PrintButton from "../../../components/buttons/PrintButton";

export default function ReportRelatorioAnalitico() {
  const [searchParams] = useSearchParams();
  const selectedPeriod = searchParams.get("period");

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const periodParts = selectedPeriod ? selectedPeriod.split("-") : [];
  const [month, year] = periodParts.map(Number);

  const isValidPeriod =
    periodParts.length === 2 &&
    !isNaN(month) &&
    !isNaN(year) &&
    month >= 1 &&
    month <= 12;

  useEffect(() => {
    if (!isValidPeriod) return;

    const reportService = new ReportService();
    setLoading(true);
    setError("");

    reportService
      .getRelatorioAnalitico(selectedPeriod)
      .then((data) => {
        setReportData(data || []);
      })
      .catch(() => setError("Erro ao carregar o relatório."))
      .finally(() => setLoading(false));
  }, [selectedPeriod, isValidPeriod]);

  const formatDateTime = (date, startHour, endHour) => {
    try {
      // If startHour contains a full ISO datetime, use it directly
      // Otherwise, combine the date with the time from startHour
      let startDateTime;
      let endDateTime;
      
      if (startHour && startHour.includes('T')) {
        // startHour is a full ISO datetime
        startDateTime = DateTime.fromISO(startHour, { zone: "UTC" })
          .setZone("America/Sao_Paulo");
      } else if (date && startHour) {
        // Combine date with time
        const dateOnly = DateTime.fromISO(date, { zone: "UTC" }).toISODate();
        startDateTime = DateTime.fromISO(`${dateOnly}T${startHour}`, { zone: "UTC" })
          .setZone("America/Sao_Paulo");
      } else {
        throw new Error("Invalid date/time format");
      }
      
      if (endHour && endHour.includes('T')) {
        // endHour is a full ISO datetime
        endDateTime = DateTime.fromISO(endHour, { zone: "UTC" })
          .setZone("America/Sao_Paulo");
      } else if (date && endHour) {
        // Combine date with time
        const dateOnly = DateTime.fromISO(date, { zone: "UTC" }).toISODate();
        endDateTime = DateTime.fromISO(`${dateOnly}T${endHour}`, { zone: "UTC" })
          .setZone("America/Sao_Paulo");
      } else {
        throw new Error("Invalid date/time format");
      }

      const formattedStart = startDateTime
        .setLocale("pt-BR")
        .toFormat("ccc dd/MM/yyyy HH:mm")
        .toUpperCase()
        .replace(/\./, "");

      const formattedEnd = endDateTime.toFormat("HH:mm");

      return `${formattedStart} ÀS ${formattedEnd}`;
    } catch (error) {
      console.error("Error formatting datetime:", error);
      return "Data inválida";
    }
  };

  const formatHour = (hour) => {
    return hour
      ? DateTime.fromISO(hour, { zone: "UTC" })
          .setZone("America/Sao_Paulo")
          .toFormat("HH:mm")
      : "";
  };

  if (!isValidPeriod) {
    return (
      <div className="text-danger mt-3">Erro: Formato do período inválido.</div>
    );
  }

  const periodDate = DateTime.fromObject({ year, month }, { zone: "America/Sao_Paulo" });

  const monthName = periodDate
    .setLocale("pt-BR")
    .toFormat("MMMM")
    .toUpperCase();
  const startDate = periodDate.startOf("month").toFormat("dd/MM/yyyy");
  const endDate = periodDate.endOf("month").toFormat("dd/MM/yyyy");

  const yearAndMonth = periodDate.toFormat("yyyy-MM");

  const groupedByGuard = reportData.reduce((acc, group) => {
    if (!acc[group.name]) acc[group.name] = [];
    acc[group.name].push(group);
    return acc;
  }, {});

  return (
    <div>
      <div className="container mt-4">
        <PrintButton
          fileName={`${yearAndMonth} - FECHAMENTO - RELATÓRIO ANALÍTICO`}
        />

        <div className="row">
          <div className="col-3">
            <img src="/logo.png" alt="Logo" width="120" />
          </div>

          <div className="col-9 text-center">
            <h3 className="fw-bold black-text">
              GAMA - GUARDA MUNICIPAL DE AMERICANA
            </h3>
            <h3 className="black-text">
              SISTEMA DE DIÁRIA ADICIONAL POR ESCALA EXTRAORDINÁRIA
            </h3>
            <h3 className="black-text">FECHAMENTO - RELATÓRIO ANALÍTICO</h3>
          </div>

          <div
            className="col-12 text-end p-2 black-text"
            style={{
              display: "inline-block",
              width: "fit-content",
              marginLeft: "auto",
            }}
          >
            <div className="text-center black-text">
              MÊS {monthName} {year}
            </div>
            <div className="black-text">
              DE: {startDate} A {endDate}
            </div>
          </div>
        </div>

        {error && <div className="text-danger mt-3">{error}</div>}
        {loading && <p>Carregando...</p>}

        {Object.entries(groupedByGuard).map(([guardName, schools]) => (
          <div key={guardName} className="mb-5">
            <h4 className="black-text">
              {guardName.toUpperCase()} - MATRÍCULA: {schools[0].username}
            </h4>

            {schools.map((user, index) => (
              <div key={index} className="mb-4">
                <table className="table table-bordered custom-table">
                  <thead>
                    <tr className="bg-secondary text-white">
                      <th className="school-header" colSpan="9">
                        {user.schoolName?.toUpperCase()}
                      </th>
                    </tr>
                    <tr>
                      <th>DIA HORA</th>
                      <th>CHECKIN</th>
                      <th>CHECKOUT</th>
                      <th>LOCAL</th>
                      <th>DIA HORA</th>
                      <th>CHECKIN</th>
                      <th>CHECKOUT</th>
                      <th>LOCAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({
                      length: Math.ceil(user.reservations.length / 2),
                    }).map((_, index) => {
                      const first = user.reservations[index];
                      const second =
                        user.reservations[
                          index + Math.ceil(user.reservations.length / 2)
                        ];
                      return (
                        <tr key={index}>
                          <td>
                            {first
                              ? formatDateTime(
                                  first.date,
                                  first.startHour,
                                  first.endHour
                                )
                              : ""}
                          </td>
                          <td>
                            {first?.canceled
                              ? "CANCELADO"
                              : formatHour(first?.checkin)}
                          </td>
                          <td>{formatHour(first?.checkout)}</td>
                          <td>
                            {first?.latitude && first?.longitude && (
                              <a
                                href={`https://www.google.com/maps/search/${first.latitude},${first.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Localização
                              </a>
                            )}
                          </td>
                          <td>
                            {second
                              ? formatDateTime(
                                  second.date,
                                  second.startHour,
                                  second.endHour
                                )
                              : ""}
                          </td>
                          <td>
                            {second?.canceled
                              ? "CANCELADO"
                              : formatHour(second?.checkin)}
                          </td>
                          <td>{formatHour(second?.checkout)}</td>
                          <td>
                            {second?.latitude && second?.longitude && (
                              <a
                                href={`https://www.google.com/maps/search/${second.latitude},${second.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Localização
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ))}

        {reportData.length > 0 && (
          <div className="mt-4">
            <table className="table table-bordered custom-table">
              <tbody>
                <tr className="bg-secondary text-white">
                  <td className="school-header" colSpan="7">
                    TOTAL GERAL
                  </td>
                  <td className="school-header">
                    {reportData.reduce(
                      (total, user) => total + user.totalHours,
                      0
                    )}{" "}
                    HORAS
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
